import { Component, OnInit, ViewChild, inject, ViewEncapsulation } from '@angular/core';
import { AuthService, type IAuthFormType } from './auth.service';
import { MatCardModule } from '@angular/material/card';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormControl, FormsModule, NgModel } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { passwordMatchValidator, passwordStrengthValidator } from './validators';

interface RegisterFormModel {
  userName: FormControl<string>;
  email: FormControl<string>;
  password: FormControl<string>;
  confirmPassword: FormControl<string>;
}
@Component({
  selector: 'app-auth',
  standalone: true,
  imports: [CommonModule, MatCardModule, ReactiveFormsModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatIconModule, FormsModule],
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.css'],
  encapsulation: ViewEncapsulation.None,
})

export class AuthComponent implements OnInit {
  hide = true;
  hide2 = true;
  hideLoginPassword = true;
  confirmEmail = '';
  @ViewChild('codeInput') codeInputRef?: NgModel;
  auth = inject(AuthService);
  currentForm = this.auth.currentForm;
  registrationStep = this.auth.registrationStep;
  errorMessages = this.auth.errorMessages;
  newConfirmCode = '';


  loginForm: FormGroup;
  registerForm: FormGroup<RegisterFormModel>;
  forgotForm: FormGroup;

  constructor(private fb: FormBuilder, private router: Router, private route: ActivatedRoute) {


    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required]
    })

    this.registerForm = this.fb.nonNullable.group({
      userName: '',
      email: this.fb.control('', { nonNullable: true, validators: [Validators.email] }),
      password: this.fb.control('', { nonNullable: true, validators: [Validators.minLength(8), passwordStrengthValidator] }),
      confirmPassword: ''
    }, { validators: passwordMatchValidator }
    );

    this.forgotForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      code: [''],
      newPassword: [''],
      confirmNewPassword: ['']
    })

  }

  ngOnInit(): void {
    const queryParams = this.route.snapshot.queryParamMap;

    const mode = queryParams.get('mode');
    if (mode === 'login' || mode === 'register' || mode === 'forgot' || mode === 'confirm') {
      this.auth.setCurrentForm(mode);
    }

    const email = queryParams.get('email');
    const code = queryParams.get('code');

    if (email) {
      this.confirmEmail = email;
    }

    if (code && /^[0-9]{6}$/.test(code)) {
      this.newConfirmCode = code;
      this.auth.confirmRegistration(code, email || this.confirmEmail);
    }
  }

  handleChangeAuthMode(mode: IAuthFormType) {
    if (mode === 'confirm') {
      this.confirmEmail = this.auth.registerUser().email;
      this.newConfirmCode = '';
    }
    if (mode !== 'forgot') {
      this.auth.passwordRequested.set(false);
      this.forgotForm.reset();
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { mode },
      queryParamsHandling: 'merge',
    })
    this.auth.setCurrentForm(mode);
    this.auth.errorMessages.set(null);
  }

  submitRegisterForm() {

    const { email, userName, password } = this.registerForm.value

    if (!this.registerForm.valid || !email || !password ) {
      return;
    }

    this.auth.register({
      email,
      userName: userName ?? '',
      password
    }).then((result) => {

      if (result === true) {
        this.handleChangeAuthMode('confirm')
      }

    })
  }

  handlePressEnter(event:KeyboardEvent) {
    if(event.key === 'Enter') {
      this.submit();
    }
  }

  handleConfirmCodeChange(value: string) {
    if (/[^0-9]/.test(value)) {
      this.codeInputRef?.control.markAsTouched();
      return;
    }
    if (value.length === 6) {
      this.auth.confirmRegistration(value, this.confirmEmail);
    }
  }

  submitLoginForm() {
    const {email, password} = this.loginForm.value
    this.auth.loginUser(email, password);
  }

  async submitForgotPassword() {
    const { email } = this.forgotForm.value;
    
    if (!this.forgotForm.valid || !email) {
      return;
    }

    this.auth.errorMessages.set(null);

    await this.auth.forgotPassword(email);
  }

  submitSetNewPassword() {
    const { email, code, newPassword } = this.forgotForm.value;
    this.auth.setNewPassword(email, code, newPassword);
  }

  submit() {
    const activeForm = this.currentForm();
    if (activeForm === 'login') return this.submitLoginForm();
    if (activeForm === 'forgot' && this.auth.passwordRequested()) return this.submitSetNewPassword();
    if (activeForm === 'forgot') return this.submitForgotPassword();
    if (activeForm === 'register') return this.submitRegisterForm()
  }

}